import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { PostShipmentsV3, PostExecutionLegs } from '@/apis/shipment';
import type {
  ShipmentHeader,
  ShipmentReportResponse,
  ShipmentRequestBody,
  ShipmentRow,
} from '../types';
import { normalizeShipmentHeaders } from '../components/shipment/shipmentTableUtils';

interface UseShipmentReportProps {
  token: string;
  payload: Record<string, unknown> | null;
}

export interface ShipmentReportPage {
  headers: ShipmentHeader[];
  rows: ShipmentRow[];
  total: number | null;
  overAllShipmentCount: number | null;
  totalPages: number;
  page: number;
  countPending: boolean;
}

const getBasePayload = (
  payload: Record<string, unknown> | null
): Record<string, unknown> | null => {
  if (!payload) return null;

  const { page: _page, ...basePayload } = payload;
  return basePayload;
};

export const parseShipmentReportPage = (
  res: ShipmentReportResponse,
  page: number
): ShipmentReportPage => {
  const payload = res.data.data as unknown as Record<string, unknown>;
  const inner = (
    'data' in payload && !Array.isArray(payload.data) ? payload.data : payload
  ) as Record<string, unknown>;
  const rows = Array.isArray(inner.data)
    ? (inner.data as ShipmentRow[])
    : Array.isArray(inner)
      ? (inner as ShipmentRow[])
      : [];
  const headers = normalizeShipmentHeaders(
    (inner.active_columns || inner.header) as ShipmentHeader[] | undefined
  );
  const countPending = Boolean(inner.count_pending);
  const rawTotal =
    inner.total_shipment_count ?? inner.total_count ?? inner.total ?? null;
  const total = countPending ? null : Number(rawTotal || 0) || rows.length;
  const overAllShipmentCount = countPending
    ? null
    : Number(inner.over_all_shipment_count || 0) || null;

  return {
    headers,
    rows,
    total,
    overAllShipmentCount,
    totalPages: Number(inner.page_count || inner.total_pages || 1),
    page,
    countPending,
  };
};

interface UseShipmentReportCountProps {
  token: string;
  payload: Record<string, unknown> | null;
  enabled: boolean;
}

export const useShipmentReportCount = ({
  token,
  payload,
  enabled,
}: UseShipmentReportCountProps) => {
  const basePayload = getBasePayload(payload);

  return useQuery({
    queryKey: ['shipment-report-count', JSON.stringify(basePayload)],
    queryFn: async (): Promise<{
      total: number;
      overAllShipmentCount: number | null;
    }> => {
      const fetchFn =
        basePayload?.leg_type === 'EL' ? PostExecutionLegs : PostShipmentsV3;
      const res = (await fetchFn(token, {
        ...(basePayload as ShipmentRequestBody),
        page: 1,
        count_only: true,
      })) as ShipmentReportResponse;

      const page = parseShipmentReportPage(res, 1);
      return {
        total: page.total ?? 0,
        overAllShipmentCount: page.overAllShipmentCount,
      };
    },
    enabled: Boolean(token && basePayload && enabled),
    staleTime: 30_000,
    gcTime: 60_000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};

export const useShipmentReport = ({
  token,
  payload,
}: UseShipmentReportProps) => {
  const basePayload = getBasePayload(payload);

  return useInfiniteQuery({
    queryKey: ['shipment-report', JSON.stringify(basePayload)],
    initialPageParam: 1,
    queryFn: async ({ pageParam }): Promise<ShipmentReportPage> => {
      const page = Number(pageParam) || 1;
      const fetchFn =
        basePayload?.leg_type === 'EL' ? PostExecutionLegs : PostShipmentsV3;
      const res = (await fetchFn(token, {
        ...(basePayload as ShipmentRequestBody),
        page,
      })) as ShipmentReportResponse;

      return parseShipmentReportPage(res, page);
    },
    getNextPageParam: (lastPage) =>
      lastPage.page < lastPage.totalPages ? lastPage.page + 1 : undefined,
    staleTime: 30_000,
    gcTime: 60_000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: Boolean(token && basePayload),
  });
};
